import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDemo } from '../contexts/DemoContext';
import { buildApiUrl } from '../config/api';

export const useDebts = () => {
  const { getAuthHeaders, user } = useAuth();
  const { isDemoModeActive, getDemoQueryParams } = useDemo();
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all debts
  const fetchDebts = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const queryParams = getDemoQueryParams();
      const response = await fetch(buildApiUrl(`/api/debt${queryParams}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        setDebts(result.data || []);
      } else {
        setError(result.error || 'Failed to fetch debts');
      }
    } catch (err) {
      console.error('Error fetching debts:', err);
      setError('Failed to fetch debts');
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeaders, getDemoQueryParams]);

  // Create new debt
  const createDebt = useCallback(async (debtData) => {
    if (!user) throw new Error('No authentication token');
    if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');

    try {
      const response = await fetch(buildApiUrl('/api/debt'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(debtData),
      });

      const result = await response.json();

      if (result.success) {
        await fetchDebts(); // Refresh the list
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to create debt');
      }
    } catch (err) {
      console.error('Error creating debt:', err);
      throw err;
    }
  }, [user, getAuthHeaders, fetchDebts, isDemoModeActive]);

  // Update existing debt
  const updateDebt = useCallback(async (id, debtData) => {
    if (!user) throw new Error('No authentication token');
    if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');

    try {
      const response = await fetch(buildApiUrl(`/api/debt/${id}`), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(debtData),
      });

      const result = await response.json();

      if (result.success) {
        await fetchDebts(); // Refresh the list
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to update debt');
      }
    } catch (err) {
      console.error('Error updating debt:', err);
      throw err;
    }
  }, [user, getAuthHeaders, fetchDebts, isDemoModeActive]);

  // Delete debt
  const deleteDebt = useCallback(async (id) => {
    if (!user) throw new Error('No authentication token');
    if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');

    try {
      console.log('useDebts: Deleting debt with ID:', id);
      console.log('useDebts: API URL:', buildApiUrl(`/api/debt/${id}`));
      console.log('useDebts: Auth headers:', getAuthHeaders());
      
      const response = await fetch(buildApiUrl(`/api/debt/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      console.log('useDebts: Response status:', response.status);
      console.log('useDebts: Response ok:', response.ok);

      const result = await response.json();
      console.log('useDebts: Response result:', result);

      if (result.success) {
        console.log('useDebts: Delete successful, refreshing debts list');
        await fetchDebts(); // Refresh the list
        return true;
      } else {
        console.error('useDebts: Delete failed:', result.error);
        throw new Error(result.error || 'Failed to delete debt');
      }
    } catch (err) {
      console.error('useDebts: Error deleting debt:', err);
      throw err;
    }
  }, [user, getAuthHeaders, fetchDebts, isDemoModeActive]);

  // Get debt summary
  const getDebtSummary = useCallback(async () => {
    if (!user) return null;

    try {
      const queryParams = getDemoQueryParams();
      const response = await fetch(buildApiUrl(`/api/debt/summary/overview${queryParams}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to fetch debt summary');
      }
    } catch (err) {
      console.error('Error fetching debt summary:', err);
      throw err;
    }
  }, [user, getAuthHeaders, getDemoQueryParams]);

  // Get debts by type
  const getDebtsByType = useCallback(async (type) => {
    if (!user) return [];

    try {
      const queryParams = getDemoQueryParams();
      const response = await fetch(buildApiUrl(`/api/debt/type/${encodeURIComponent(type)}${queryParams}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        return result.data || [];
      } else {
        throw new Error(result.error || 'Failed to fetch debts by type');
      }
    } catch (err) {
      console.error('Error fetching debts by type:', err);
      throw err;
    }
  }, [user, getAuthHeaders, getDemoQueryParams]);

  // Calculate summary statistics
  const calculateSummary = useCallback(() => {
    const totalOutstanding = debts.reduce((sum, debt) => sum + (debt.outstanding || 0), 0);
    const totalMonthlyEMI = debts.reduce((sum, debt) => sum + (debt.monthlyEMI || 0), 0);
    const avgInterestRate = debts.length > 0 
      ? debts.reduce((sum, debt) => sum + (debt.interestRate || 0), 0) / debts.length 
      : 0;
    const highInterestDebt = debts
      .filter(debt => (debt.interestRate || 0) > 12)
      .reduce((sum, debt) => sum + (debt.outstanding || 0), 0);

    // Group by type
    const debtsByType = debts.reduce((acc, debt) => {
      const type = debt.type || 'Other';
      if (!acc[type]) acc[type] = [];
      acc[type].push(debt);
      return acc;
    }, {});

    return {
      totalDebts: debts.length,
      totalOutstanding,
      totalMonthlyEMI,
      avgInterestRate: Math.round(avgInterestRate * 100) / 100,
      highInterestDebt,
      debtsByType,
      debtTypes: Object.keys(debtsByType)
    };
  }, [debts]);

  // Load debts on mount and when user or demo mode changes
  useEffect(() => {
    if (user) {
      fetchDebts();
    } else {
      setDebts([]);
      setLoading(false);
    }
  }, [user, fetchDebts, isDemoModeActive]);

  return {
    debts,
    loading,
    error,
    createDebt,
    updateDebt,
    deleteDebt,
    getDebtSummary,
    getDebtsByType,
    calculateSummary,
    refreshDebts: fetchDebts,
  };
};
